import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, ChevronDownIcon, DollarSignIcon, HomeIcon, ClipboardListIcon, UsersIcon, ScaleIcon, HeartHandshakeIcon, FileTextIcon, DownloadIcon, AlertTriangleIcon, BanknoteIcon, ArrowUpIcon, CheckCircleIcon, HelpCircleIcon, XCircleIcon, GavelIcon, CoinsIcon, PercentIcon } from 'lucide-react';
interface Report {
  id: string;
  title: string;
  description: string;
  category: string;
  frequency: string;
  format: string[];
}
const reports: Report[] = [
// Revenue & Financial Reports
{
  id: 'rf1',
  title: 'Revenue Summary Report',
  description: 'Total revenue breakdown by period, type, and jurisdiction',
  category: 'Revenue & Financial',
  frequency: 'Daily, Weekly, Monthly',
  format: ['PDF', 'Excel', 'CSV']
}, {
  id: 'rf2',
  title: 'Property Tax Analysis',
  description: 'Property tax collection rates, arrears, and assessments',
  category: 'Revenue & Financial',
  frequency: 'Monthly',
  format: ['PDF', 'Excel']
}, {
  id: 'rf3',
  title: 'Service Fee Collection',
  description: 'Service fees billed vs collected by service type',
  category: 'Revenue & Financial',
  frequency: 'Weekly',
  format: ['PDF', 'Excel']
}, {
  id: 'rf4',
  title: 'Payment Reconciliation',
  description: 'Payment gateway vs system reconciliation status',
  category: 'Revenue & Financial',
  frequency: 'Daily',
  format: ['Excel', 'CSV']
}, {
  id: 'rf5',
  title: 'Exemption & Waiver Reports',
  description: 'Analysis of tax exemptions and fee waivers granted by category and jurisdiction',
  category: 'Revenue & Financial',
  frequency: 'Monthly',
  format: ['PDF', 'Excel', 'CSV']
}, {
  id: 'rf6',
  title: 'Fines & Penalties Report',
  description: 'Comprehensive report on assessed fines, penalties, and collection status',
  category: 'Revenue & Financial',
  frequency: 'Weekly',
  format: ['PDF', 'Excel']
},
// Property & Valuation Reports
{
  id: 'pv1',
  title: 'Property Registry Summary',
  description: 'Overview of registered properties by type and jurisdiction',
  category: 'Property & Valuation',
  frequency: 'Monthly',
  format: ['PDF', 'Excel']
}, {
  id: 'pv2',
  title: 'Valuation Assessment',
  description: 'Property valuations, changes, and appeals',
  category: 'Property & Valuation',
  frequency: 'Quarterly',
  format: ['PDF', 'Excel']
}, {
  id: 'pv3',
  title: 'Land Title Status',
  description: 'Land title issuance and ownership statistics',
  category: 'Property & Valuation',
  frequency: 'Monthly',
  format: ['PDF', 'Excel']
},
// Service Delivery & Operational Reports
{
  id: 'sd1',
  title: 'Service Request Analytics',
  description: 'Service request volumes, processing times, and trends',
  category: 'Service Delivery & Operations',
  frequency: 'Weekly',
  format: ['PDF', 'Excel', 'CSV']
}, {
  id: 'sd2',
  title: 'Workflow Performance',
  description: 'SLA compliance and workflow bottleneck analysis',
  category: 'Service Delivery & Operations',
  frequency: 'Weekly',
  format: ['PDF', 'Excel']
}, {
  id: 'sd3',
  title: 'Permit & License Status',
  description: 'Permit/license issuance, expiry, and processing times',
  category: 'Service Delivery & Operations',
  frequency: 'Daily',
  format: ['PDF', 'Excel']
},
// User, Role & System Administration
{
  id: 'ur1',
  title: 'User Activity Log',
  description: 'User login history and action audit trail',
  category: 'User & System Administration',
  frequency: 'Daily',
  format: ['PDF', 'Excel', 'CSV']
}, {
  id: 'ur2',
  title: 'Role & Permission Matrix',
  description: 'User roles and permission assignments',
  category: 'User & System Administration',
  frequency: 'Monthly',
  format: ['PDF', 'Excel']
}, {
  id: 'ur3',
  title: 'System Audit Trail',
  description: 'Critical system events and configuration changes',
  category: 'User & System Administration',
  frequency: 'Daily',
  format: ['PDF', 'CSV']
},
// Compliance & Enforcement
{
  id: 'ce1',
  title: 'Bylaw Violation Summary',
  description: 'Violation statistics and enforcement status',
  category: 'Compliance & Enforcement',
  frequency: 'Weekly',
  format: ['PDF', 'Excel']
}, {
  id: 'ce2',
  title: 'License Compliance',
  description: 'Business license and permit compliance status',
  category: 'Compliance & Enforcement',
  frequency: 'Weekly',
  format: ['PDF', 'Excel']
},
// Citizen Engagement
{
  id: 'cz1',
  title: 'Portal Usage Analytics',
  description: 'Citizen portal registration and service usage statistics',
  category: 'Citizen Engagement',
  frequency: 'Monthly',
  format: ['PDF', 'Excel']
}, {
  id: 'cz2',
  title: 'Feedback Analysis',
  description: 'Citizen feedback and complaint summary',
  category: 'Citizen Engagement',
  frequency: 'Monthly',
  format: ['PDF', 'Excel']
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Revenue & Financial':
      return <DollarSignIcon className="h-6 w-6 text-green-600" data-id="element-4208" />;
    case 'Property & Valuation':
      return <HomeIcon className="h-6 w-6 text-blue-600" data-id="element-4209" />;
    case 'Service Delivery & Operations':
      return <ClipboardListIcon className="h-6 w-6 text-purple-600" data-id="element-4210" />;
    case 'User & System Administration':
      return <UsersIcon className="h-6 w-6 text-orange-600" data-id="element-4211" />;
    case 'Compliance & Enforcement':
      return <ScaleIcon className="h-6 w-6 text-red-600" data-id="element-4212" />;
    case 'Citizen Engagement':
      return <HeartHandshakeIcon className="h-6 w-6 text-pink-600" data-id="element-4213" />;
    default:
      return <FileTextIcon className="h-6 w-6 text-gray-600" data-id="element-4214" />;
  }
};
const ReportCard = ({
  report,
  onReportClick
}: {
  report: Report;
  onReportClick: (report: Report) => void;
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-500 hover:shadow-sm transition-all duration-200" data-id="element-4215">
      <div className="p-5" data-id="element-4216">
        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-4217">
          {report.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-4218">{report.description}</p>
        <div className="flex items-center justify-between" data-id="element-4219">
          <div className="flex items-center space-x-4 text-sm text-gray-500" data-id="element-4220">
            <div className="flex items-center" data-id="element-4221">
              <span className="inline-block w-4 h-4 text-gray-400 mr-1.5" data-id="element-4222">
                🕒
              </span>
              <span data-id="element-4223">{report.frequency}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2" data-id="element-4224">
            {report.format.map(format => <span key={format} className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800" data-id="element-4225">
                {format}
              </span>)}
          </div>
        </div>
        <button className="mt-4 w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" onClick={() => onReportClick(report)} data-id="element-4226">
          <DownloadIcon className="h-4 w-4 mr-2" data-id="element-4227" />
          {report.category === 'Revenue & Financial' && report.title === 'Revenue Summary Report' ? 'View Revenue Summary' : report.category === 'Revenue & Financial' && report.title === 'Service Fee Collection' ? 'View Service Fee Analytics' : report.category === 'Revenue & Financial' && report.title === 'Property Tax Analysis' ? 'View Property Tax Analytics' : report.category === 'Revenue & Financial' && report.title === 'Payment Reconciliation' ? 'View Payment & Reconciliation Center' : report.category === 'Revenue & Financial' && report.title === 'Exemption & Waiver Reports' ? 'View Exemption & Waiver Reports' : report.category === 'Revenue & Financial' && report.title === 'Fines & Penalties Report' ? 'View Fines & Penalties Report' : `View ${report.title}`}
        </button>
      </div>
    </div>;
};
const CategorySection = ({
  category,
  reports,
  expanded,
  onToggle,
  onReportClick
}: {
  category: string;
  reports: Report[];
  expanded: boolean;
  onToggle: () => void;
  onReportClick: (report: Report) => void;
}) => {
  return <div className="mb-8" data-id="element-4228">
      <button onClick={onToggle} className="flex items-center w-full text-left mb-4" data-id="element-4229">
        <CategoryIcon category={category} data-id="element-4230" />
        <h2 className="text-xl font-semibold text-gray-900 ml-2 flex-1" data-id="element-4231">
          {category}
        </h2>
        {expanded ? <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-4232" /> : <ChevronRightIcon className="h-5 w-5 text-gray-500" data-id="element-4233" />}
      </button>
      {expanded && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4234">
          {reports.map(report => <ReportCard key={report.id} report={report} onReportClick={onReportClick} data-id="element-4235" />)}
        </div>}
    </div>;
};
export const ReportsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [showRevenueSummary, setShowRevenueSummary] = useState(false);
  const [showServiceFeeAnalytics, setShowServiceFeeAnalytics] = useState(false);
  const [showPropertyTaxAnalytics, setShowPropertyTaxAnalytics] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const filteredReports = reports.filter(report => report.title.toLowerCase().includes(searchQuery.toLowerCase()) || report.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredReports.map(report => report.category)));
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const handleReportClick = (report: Report) => {
    setSelectedReport(report);
    if (report.category === 'Revenue & Financial' && report.title === 'Revenue Summary Report') {
      setShowRevenueSummary(true);
    } else if (report.category === 'Revenue & Financial' && report.title === 'Service Fee Collection') {
      setShowServiceFeeAnalytics(true);
    } else if (report.category === 'Revenue & Financial' && report.title === 'Property Tax Analysis') {
      setShowPropertyTaxAnalytics(true);
    } else if (report.category === 'Revenue & Financial' && report.title === 'Payment Reconciliation') {
      setShowRevenueSummary(true);
    } else if (report.category === 'Revenue & Financial' && report.title === 'Exemption & Waiver Reports') {
      setShowRevenueSummary(true);
    } else if (report.category === 'Revenue & Financial' && report.title === 'Fines & Penalties Report') {
      setShowRevenueSummary(true);
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-4236">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4237">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-4238">
          <a href="/" className="hover:text-gray-700" data-id="element-4239">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-4240" />
          <span className="text-gray-900" data-id="element-4241">Reports</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-4242">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4243">Reports</h1>
          <p className="text-gray-600" data-id="element-4244">
            Access and generate system reports across all service areas
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-4245">
          <div className="max-w-md relative" data-id="element-4246">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4247">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-4248" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search reports..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4249" />
          </div>
        </div>
        {/* Report Categories */}
        {categories.map(category => <CategorySection key={category} category={category} reports={filteredReports.filter(report => report.category === category)} expanded={expandedCategories.includes(category)} onToggle={() => toggleCategory(category)} onReportClick={handleReportClick} data-id="element-4250" />)}
        {showRevenueSummary && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto" data-id="element-4251">
            <div className="min-h-screen px-4 text-center" data-id="element-4252">
              <div className="fixed inset-0" onClick={() => setShowRevenueSummary(false)} data-id="element-4253"></div>
              <div className="inline-block w-full max-w-7xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg" data-id="element-4254">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200" data-id="element-4255">
                  <div data-id="element-4256">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4257">
                      Admin{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4258" />{' '}
                      Reports{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4259" />{' '}
                      Financial{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4260" />{' '}
                      Revenue Summary
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-4261">
                      Revenue Summary Report
                    </h2>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-4262">
                    Data as of: 2024-07-27 10:35 AM
                  </div>
                </div>
                {/* Filter Panel */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-4263">
                  <div className="flex items-center justify-between mb-4" data-id="element-4264">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-4265">
                      Filter Report
                    </h3>
                    <div className="space-x-2" data-id="element-4266">
                      <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4267">
                        Reset Filters
                      </button>
                      <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-4268">
                        Apply Filters
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4269">
                    {/* Date Range */}
                    <div data-id="element-4270">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4271">
                        Date Range
                      </label>
                      <div className="flex space-x-2" data-id="element-4272">
                        <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Start Date" data-id="element-4273" />
                        <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="End Date" data-id="element-4274" />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2" data-id="element-4275">
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4276">
                          Last 7 Days
                        </button>
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4277">
                          Last 30 Days
                        </button>
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4278">
                          This Month
                        </button>
                      </div>
                    </div>
                    {/* Region */}
                    <div data-id="element-4279">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4280">
                        Region
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4281">
                        <option value="" data-id="element-4282">All Regions</option>
                        <option value="oromia" data-id="element-4283">Oromia</option>
                        <option value="amhara" data-id="element-4284">Amhara</option>
                        <option value="tigray" data-id="element-4285">Tigray</option>
                        <option value="snnpr" data-id="element-4286">SNNPR</option>
                      </select>
                    </div>
                    {/* Zones */}
                    <div data-id="element-4287">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4288">
                        Zone
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4289">
                        <option value="" data-id="element-4290">All Zones</option>
                        <option value="arsi" data-id="element-4291">Arsi</option>
                        <option value="bale" data-id="element-4292">Bale</option>
                        <option value="borena" data-id="element-4293">Borena</option>
                        <option value="illubabor" data-id="element-4294">Illubabor</option>
                      </select>
                    </div>
                    {/* Woredas */}
                    <div data-id="element-4295">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4296">
                        Woreda
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4297">
                        <option value="" data-id="element-4298">All Woredas</option>
                        <option value="asela" data-id="element-4299">Asela</option>
                        <option value="bekoji" data-id="element-4300">Bekoji</option>
                        <option value="diksis" data-id="element-4301">Diksis</option>
                        <option value="sire" data-id="element-4302">Sire</option>
                      </select>
                    </div>
                    {/* Revenue Type */}
                    <div data-id="element-4303">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4304">
                        Revenue Type
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4305">
                        <option value="" data-id="element-4306">All Revenue Types</option>
                        <option value="property" data-id="element-4307">
                          Ad Valorem Property Tax
                        </option>
                        <option value="waste" data-id="element-4308">Waste Management Fee</option>
                        <option value="permit" data-id="element-4309">Building Permit Fee</option>
                        <option value="penalty" data-id="element-4310">Late Payment Penalty</option>
                      </select>
                    </div>
                    {/* Service Category */}
                    <div data-id="element-4311">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4312">
                        Service Category
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4313">
                        <option value="" data-id="element-4314">All Categories</option>
                        <option value="property" data-id="element-4315">Property Services</option>
                        <option value="business" data-id="element-4316">Business Services</option>
                        <option value="utilities" data-id="element-4317">Utilities</option>
                        <option value="permits" data-id="element-4318">Permits & Licenses</option>
                      </select>
                    </div>
                    {/* Payment Method */}
                    <div data-id="element-4319">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4320">
                        Payment Method
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4321">
                        <option value="" data-id="element-4322">All Payment Methods</option>
                        <option value="cash" data-id="element-4323">Cash</option>
                        <option value="telebirr" data-id="element-4324">TeleBirr</option>
                        <option value="cbe" data-id="element-4325">CBE Birr</option>
                        <option value="bank" data-id="element-4326">Bank Transfer</option>
                        <option value="card" data-id="element-4327">Card Payment</option>
                      </select>
                    </div>
                    {/* Status */}
                    <div data-id="element-4328">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4329">
                        Status
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4330">
                        <option value="" data-id="element-4331">All Statuses</option>
                        <option value="completed" data-id="element-4332">Completed</option>
                        <option value="pending" data-id="element-4333">Pending</option>
                        <option value="failed" data-id="element-4334">Failed</option>
                        <option value="refunded" data-id="element-4335">Refunded</option>
                      </select>
                    </div>
                  </div>
                </div>
                {/* KPI Cards */}
                <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4336">
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4337">
                    <div className="flex items-center justify-between mb-2" data-id="element-4338">
                      <GavelIcon className="h-8 w-8 text-blue-500" data-id="element-4339" />
                      <span className="text-xs text-gray-600" data-id="element-4340">315 items</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4341">
                      250,700.00 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4342">
                      Total Revenue
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4343">
                    <div className="flex items-center justify-between mb-2" data-id="element-4344">
                      <CoinsIcon className="h-8 w-8 text-green-500" data-id="element-4345" />
                      <span className="text-xs text-gray-600" data-id="element-4346">220 items</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4347">
                      9,200,100.00 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4348">
                      Property Tax Contributio
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4349">
                    <div className="flex items-center justify-between mb-2" data-id="element-4350">
                      <PercentIcon className="h-8 w-8 text-purple-500" data-id="element-4351" />
                      <span className="text-xs text-purple-600" data-id="element-4352">+2.5%</span>
                    </div>
                    <div className="text-2xl font-bold text-green-600" data-id="element-4353">
                      71.9%
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4354">Collection Rate</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4355">
                    <div className="flex items-center justify-between mb-2" data-id="element-4356">
                      <AlertTriangleIcon className="h-8 w-8 text-red-500" data-id="element-4357" />
                      <span className="text-xs text-red-600" data-id="element-4358">95 items</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4359">
                      70,450.00 ETB
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4360">
                      <div className="text-sm text-gray-500" data-id="element-4361">
                        Currently Outstanding
                      </div>
                      <button className="text-xs text-red-600 hover:text-red-700 font-medium" data-id="element-4362">
                        View Outstanding Details
                      </button>
                    </div>
                  </div>
                </div>
                {/* Transactions Table */}
                <div className="px-6 py-4" data-id="element-4363">
                  <div className="flex items-center justify-between mb-4" data-id="element-4364">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-4365">
                      Detailed Transactions
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-4366">
                      <select className="text-sm border border-gray-300 rounded-md" data-id="element-4367">
                        <option data-id="element-4368">Show 10</option>
                        <option data-id="element-4369">Show 25</option>
                        <option data-id="element-4370">Show 50</option>
                      </select>
                      <input type="search" placeholder="Search transactions..." className="text-sm border border-gray-300 rounded-md px-3 py-2" data-id="element-4371" />
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-4372">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-4373">
                      <thead className="bg-gray-50" data-id="element-4374">
                        <tr data-id="element-4375">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4376">
                            Payment Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4377">
                            Transaction ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4378">
                            Payer Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4379">
                            Revenue Type
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4380">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-4381">
                        <tr data-id="element-4382">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4383">
                            2024-07-27
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-4384">
                            TXN-001234
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4385">
                            John Doe
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4386">
                            Property Tax
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right" data-id="element-4387">
                            15,000 ETB
                          </td>
                        </tr>
                        {/* Add more rows as needed */}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-4" data-id="element-4388">
                    <div className="text-sm text-gray-700" data-id="element-4389">
                      Showing 1 to 10 of 50 entries
                    </div>
                    <div className="flex space-x-2" data-id="element-4390">
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4391">
                        Previous
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-blue-50 text-blue-600" data-id="element-4392">
                        1
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4393">
                        2
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4394">
                        Next
                      </button>
                    </div>
                  </div>
                </div>
                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between" data-id="element-4395">
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" onClick={() => setShowRevenueSummary(false)} data-id="element-4396">
                    Close
                  </button>
                  <div className="space-x-2" data-id="element-4397">
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4398">
                      Print Report
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4399">
                      Export PDF
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4400">
                      Export CSV
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {showServiceFeeAnalytics && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto" data-id="element-4401">
            <div className="min-h-screen px-4 text-center" data-id="element-4402">
              <div className="fixed inset-0" onClick={() => setShowServiceFeeAnalytics(false)} data-id="element-4403"></div>
              <div className="inline-block w-full max-w-7xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg" data-id="element-4404">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200" data-id="element-4405">
                  <div data-id="element-4406">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4407">
                      Admin{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4408" />{' '}
                      Reports{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4409" />{' '}
                      Financial{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4410" />{' '}
                      Service Fee Analytics
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-4411">
                      Service Fee Reports
                    </h2>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-4412">
                    Data as of: 2024-07-27 11:45 AM
                  </div>
                </div>
                {/* Filter Panel */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-4413">
                  <div className="flex items-center justify-between mb-4" data-id="element-4414">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-4415">
                      Filter Service Fee Data
                    </h3>
                    <div className="space-x-2" data-id="element-4416">
                      <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4417">
                        Reset Filters
                      </button>
                      <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-4418">
                        Apply Filters
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4419">
                    {/* Date Range */}
                    <div data-id="element-4420">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4421">
                        Date Range
                      </label>
                      <div className="flex space-x-2" data-id="element-4422">
                        <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Start Date" data-id="element-4423" />
                        <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="End Date" data-id="element-4424" />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2" data-id="element-4425">
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4426">
                          Last 30 Days
                        </button>
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4427">
                          This Month
                        </button>
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4428">
                          Last Month
                        </button>
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4429">
                          This Quarter
                        </button>
                        <button className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4430">
                          Year to Date
                        </button>
                      </div>
                    </div>
                    {/* Jurisdiction Hierarchy */}
                    <div data-id="element-4431">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4432">
                        Region
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4433">
                        <option value="" data-id="element-4434">All Regions</option>
                        <option value="oromia" data-id="element-4435">Oromia</option>
                        <option value="amhara" data-id="element-4436">Amhara</option>
                      </select>
                    </div>
                    <div data-id="element-4437">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4438">
                        Zone
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4439">
                        <option value="" data-id="element-4440">All Zones</option>
                        <option value="arsi" data-id="element-4441">Arsi</option>
                        <option value="bale" data-id="element-4442">Bale</option>
                      </select>
                    </div>
                    <div data-id="element-4443">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4444">
                        Woreda/City
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4445">
                        <option value="" data-id="element-4446">All Woredas</option>
                        <option value="asela" data-id="element-4447">Asela</option>
                        <option value="bekoji" data-id="element-4448">Bekoji</option>
                      </select>
                    </div>
                    <div data-id="element-4449">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4450">
                        Kebele
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4451">
                        <option value="" data-id="element-4452">All Kebeles</option>
                        <option value="01" data-id="element-4453">Kebele 01</option>
                        <option value="02" data-id="element-4454">Kebele 02</option>
                      </select>
                    </div>
                    {/* Service Category */}
                    <div data-id="element-4455">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4456">
                        Service Category
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4457">
                        <option value="" data-id="element-4458">All Categories</option>
                        <option value="waste" data-id="element-4459">Waste Management Services</option>
                        <option value="water" data-id="element-4460">
                          Water & Sanitation Services
                        </option>
                        <option value="property" data-id="element-4461">Property Services</option>
                      </select>
                    </div>
                    {/* Specific Service */}
                    <div data-id="element-4462">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4463">
                        Specific Service
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4464">
                        <option value="" data-id="element-4465">All Services</option>
                        <option value="residential" data-id="element-4466">
                          Residential Waste Collection
                        </option>
                        <option value="commercial" data-id="element-4467">
                          Commercial Waste Collection
                        </option>
                        <option value="water" data-id="element-4468">Water Supply Service</option>
                      </select>
                    </div>
                    {/* Property Type */}
                    <div data-id="element-4469">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4470">
                        Property Type
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4471">
                        <option value="" data-id="element-4472">All Property Types</option>
                        <option value="residential" data-id="element-4473">Residential</option>
                        <option value="commercial" data-id="element-4474">Commercial</option>
                        <option value="industrial" data-id="element-4475">Industrial</option>
                      </select>
                    </div>
                    {/* Payment Status */}
                    <div data-id="element-4476">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4477">
                        Payment Status
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4478">
                        <option value="" data-id="element-4479">All Statuses</option>
                        <option value="paid" data-id="element-4480">Paid</option>
                        <option value="partial" data-id="element-4481">Partially Paid</option>
                        <option value="unpaid" data-id="element-4482">Unpaid</option>
                        <option value="overdue" data-id="element-4483">Overdue</option>
                      </select>
                    </div>
                  </div>
                </div>
                {/* KPI Cards */}
                <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4484">
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4485">
                    <div className="flex items-center justify-between mb-2" data-id="element-4486">
                      <FileTextIcon className="h-8 w-8 text-blue-500" data-id="element-4487" />
                      <span className="text-xs text-blue-600" data-id="element-4488">↑ 3.2%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4489">
                      4,500,050 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4490">
                      Total Fees Billed
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4491">
                    <div className="flex items-center justify-between mb-2" data-id="element-4492">
                      <DollarSignIcon className="h-8 w-8 text-green-500" data-id="element-4493" />
                      <span className="text-xs text-green-600" data-id="element-4494">↑ 4.5%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4495">
                      3,950,700 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4496">
                      Total Fees Collected
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4497">
                    <div className="flex items-center justify-between mb-2" data-id="element-4498">
                      <div className="h-8 w-8 text-purple-500" data-id="element-4499" />
                      <span className="text-xs text-purple-600" data-id="element-4500">↑ 1.2%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4501">
                      87.8%
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4502">Collection Rate</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4503">
                    <div className="flex items-center justify-between mb-2" data-id="element-4504">
                      <AlertTriangleIcon className="h-8 w-8 text-red-500" data-id="element-4505" />
                      <span className="text-xs text-red-600" data-id="element-4506">↓ 2.1%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4507">
                      549,350 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4508">
                      Outstanding Fees
                    </div>
                  </div>
                </div>
                {/* Detailed Table */}
                <div className="px-6 py-4" data-id="element-4509">
                  <div className="flex items-center justify-between mb-4" data-id="element-4510">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-4511">
                      Detailed Service Fee Records
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-4512">
                      <select className="text-sm border border-gray-300 rounded-md" data-id="element-4513">
                        <option data-id="element-4514">Show 10</option>
                        <option data-id="element-4515">Show 25</option>
                        <option data-id="element-4516">Show 50</option>
                      </select>
                      <input type="search" placeholder="Search records..." className="text-sm border border-gray-300 rounded-md px-3 py-2" data-id="element-4517" />
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-4518">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-4519">
                      <thead className="bg-gray-50" data-id="element-4520">
                        <tr data-id="element-4521">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4522">
                            Billing Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4523">
                            Service Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4524">
                            Property ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4525">
                            Payer Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4526">
                            Amount Billed
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4527">
                            Amount Paid
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4528">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-4529">
                        <tr data-id="element-4530">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4531">
                            2024-07-27
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4532">
                            Waste Collection
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-4533">
                            PROP-001234
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4534">
                            John Doe
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4535">
                            1,500 ETB
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4536">
                            1,500 ETB
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-4537">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-4538">
                              Paid
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-4" data-id="element-4539">
                    <div className="text-sm text-gray-700" data-id="element-4540">
                      Showing 1 to 10 of 50 entries
                    </div>
                    <div className="flex space-x-2" data-id="element-4541">
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4542">
                        Previous
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-blue-50 text-blue-600" data-id="element-4543">
                        1
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4544">
                        2
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4545">
                        Next
                      </button>
                    </div>
                  </div>
                </div>
                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between" data-id="element-4546">
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" onClick={() => setShowServiceFeeAnalytics(false)} data-id="element-4547">
                    Close
                  </button>
                  <div className="space-x-2" data-id="element-4548">
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4549">
                      Export Summary (PDF)
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4550">
                      Export Records (CSV)
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4551">
                      Print Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
        {showPropertyTaxAnalytics && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto" data-id="element-4552">
            <div className="min-h-screen px-4 text-center" data-id="element-4553">
              <div className="fixed inset-0" onClick={() => setShowPropertyTaxAnalytics(false)} data-id="element-4554"></div>
              <div className="inline-block w-full max-w-7xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg" data-id="element-4555">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200" data-id="element-4556">
                  <div data-id="element-4557">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4558">
                      Admin{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4559" />{' '}
                      Reports{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4560" />{' '}
                      Financial{' '}
                      <ChevronRightIcon className="h-4 w-4 mx-1 inline-block" data-id="element-4561" />{' '}
                      Property Tax Analytics
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900" data-id="element-4562">
                      Property Tax Specific Reports
                    </h2>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-4563">
                    Data as of: 2024-07-27 11:15 AM
                  </div>
                </div>
                {/* Filter Panel */}
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-4564">
                  <div className="flex items-center justify-between mb-4" data-id="element-4565">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-4566">
                      Filter Property Tax Data
                    </h3>
                    <div className="space-x-2" data-id="element-4567">
                      <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4568">
                        Reset Filters
                      </button>
                      <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-4569">
                        Apply Filters
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4570">
                    {/* Assessment Year */}
                    <div data-id="element-4571">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4572">
                        Assessment Year*
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4573">
                        <option value="2024" data-id="element-4574">2024</option>
                        <option value="2023" data-id="element-4575">2023</option>
                        <option value="2022" data-id="element-4576">2022</option>
                      </select>
                    </div>
                    {/* Date Range */}
                    <div className="lg:col-span-2" data-id="element-4577">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4578">
                        Date Range
                      </label>
                      <div className="flex items-center space-x-4" data-id="element-4579">
                        <div className="flex-1 flex space-x-2" data-id="element-4580">
                          <input type="date" className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4581" />
                          <input type="date" className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4582" />
                        </div>
                        <div className="flex space-x-2" data-id="element-4583">
                          <button className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4584">
                            This Quarter
                          </button>
                          <button className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4585">
                            Last Quarter
                          </button>
                          <button className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200" data-id="element-4586">
                            Year to Date
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Jurisdiction */}
                    <div data-id="element-4587">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4588">
                        Region
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4589">
                        <option value="" data-id="element-4590">All Regions</option>
                        <option value="oromia" data-id="element-4591">Oromia</option>
                        <option value="amhara" data-id="element-4592">Amhara</option>
                      </select>
                    </div>
                    <div data-id="element-4593">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4594">
                        Zone
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4595">
                        <option value="" data-id="element-4596">All Zones</option>
                        <option value="arsi" data-id="element-4597">Arsi</option>
                        <option value="bale" data-id="element-4598">Bale</option>
                      </select>
                    </div>
                    <div data-id="element-4599">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4600">
                        Woreda/City
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4601">
                        <option value="" data-id="element-4602">All Woredas</option>
                        <option value="asela" data-id="element-4603">Asela</option>
                        <option value="bekoji" data-id="element-4604">Bekoji</option>
                      </select>
                    </div>
                    {/* Property Type */}
                    <div data-id="element-4605">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4606">
                        Property Type
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4607">
                        <option value="" data-id="element-4608">All Property Types</option>
                        <option value="residential_villa" data-id="element-4609">
                          Residential Villa G+0
                        </option>
                        <option value="commercial_shop" data-id="element-4610">Commercial Shop</option>
                      </select>
                    </div>
                    {/* Property Usage Type */}
                    <div data-id="element-4611">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4612">
                        Property Usage Type
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4613">
                        <option value="" data-id="element-4614">All Usage Types</option>
                        <option value="residential" data-id="element-4615">Residential</option>
                        <option value="commercial" data-id="element-4616">Commercial</option>
                      </select>
                    </div>
                    {/* Assessment Status */}
                    <div data-id="element-4617">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4618">
                        Assessment Status
                      </label>
                      <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-4619">
                        <option value="" data-id="element-4620">All Statuses</option>
                        <option value="assessed" data-id="element-4621">Assessed</option>
                        <option value="appealed" data-id="element-4622">Appealed</option>
                        <option value="final" data-id="element-4623">Final</option>
                        <option value="paid" data-id="element-4624">Paid</option>
                      </select>
                    </div>
                  </div>
                </div>
                {/* KPI Cards */}
                <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4625">
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4626">
                    <div className="flex items-center justify-between mb-2" data-id="element-4627">
                      <FileTextIcon className="h-8 w-8 text-blue-500" data-id="element-4628" />
                      <span className="text-xs text-blue-600" data-id="element-4629">↑ 12.5%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4630">
                      9,200,100 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4631">
                      Total Tax Billed
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4632">
                    <div className="flex items-center justify-between mb-2" data-id="element-4633">
                      <DollarSignIcon className="h-8 w-8 text-green-500" data-id="element-4634" />
                      <span className="text-xs text-green-600" data-id="element-4635">↑ 15.2%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4636">
                      7,850,500 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4637">
                      Total Tax Collected
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4638">
                    <div className="flex items-center justify-between mb-2" data-id="element-4639">
                      <div className="h-8 w-8 text-purple-500" data-id="element-4640" />
                      <span className="text-xs text-purple-600" data-id="element-4641">↑ 2.3%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4642">
                      85.3%
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4643">Collection Rate</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-4644">
                    <div className="flex items-center justify-between mb-2" data-id="element-4645">
                      <AlertTriangleIcon className="h-8 w-8 text-red-500" data-id="element-4646" />
                      <span className="text-xs text-red-600" data-id="element-4647">↓ 5.1%</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900" data-id="element-4648">
                      1,349,599 ETB
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-4649">Outstanding Tax</div>
                  </div>
                </div>
                {/* Tabs */}
                <div className="px-6 py-4" data-id="element-4650">
                  <div className="border-b border-gray-200" data-id="element-4651">
                    <nav className="-mb-px flex space-x-8" data-id="element-4652">
                      <button className="border-blue-500 text-blue-600 whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm" data-id="element-4653">
                        Assessments
                      </button>
                      <button className="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm" data-id="element-4654">
                        Payments & Arrears
                      </button>
                    </nav>
                  </div>
                </div>
                {/* Detailed Table */}
                <div className="px-6 py-4" data-id="element-4655">
                  <div className="flex items-center justify-between mb-4" data-id="element-4656">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-4657">
                      Detailed Property Tax Records
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-4658">
                      <select className="text-sm border border-gray-300 rounded-md" data-id="element-4659">
                        <option data-id="element-4660">Show 10</option>
                        <option data-id="element-4661">Show 25</option>
                        <option data-id="element-4662">Show 50</option>
                      </select>
                      <input type="search" placeholder="Search records..." className="text-sm border border-gray-300 rounded-md px-3 py-2" data-id="element-4663" />
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-4664">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-4665">
                      <thead className="bg-gray-50" data-id="element-4666">
                        <tr data-id="element-4667">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4668">
                            Property ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4669">
                            Owner Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4670">
                            Property Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4671">
                            Assessed Value
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4672">
                            Tax Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4673">
                            Tax Billed
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4674">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-4675">
                        <tr data-id="element-4676">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600" data-id="element-4677">
                            PROP-001234
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4678">
                            John Doe
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4679">
                            Residential Villa
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4680">
                            2,500,000 ETB
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4681">
                            0.5%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4682">
                            12,500 ETB
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-4683">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-4684">
                              Paid
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-4" data-id="element-4685">
                    <div className="text-sm text-gray-700" data-id="element-4686">
                      Showing 1 to 10 of 50 entries
                    </div>
                    <div className="flex space-x-2" data-id="element-4687">
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4688">
                        Previous
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-blue-50 text-blue-600" data-id="element-4689">
                        1
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4690">
                        2
                      </button>
                      <button className="px-3 py-1 text-sm border border-gray-300 rounded-md" data-id="element-4691">
                        Next
                      </button>
                    </div>
                  </div>
                </div>
                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between" data-id="element-4692">
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" onClick={() => setShowPropertyTaxAnalytics(false)} data-id="element-4693">
                    Close
                  </button>
                  <div className="space-x-2" data-id="element-4694">
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4695">
                      Export Summary (PDF)
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4696">
                      Export Assessments (CSV)
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4697">
                      Export Arrears (CSV)
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4698">
                      Print Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
};